import type { GpsPeer, SelfPosition } from './gpsPeer';
import type { NearbyPeer, PeerId } from '../types';
import { resolveCanonicalPeerId } from './peerId';

export const EARTH_RADIUS_METERS = 6_371_000;

export function destinationPoint(
  lat: number,
  lng: number,
  distanceMeters: number,
  bearingDegrees: number,
): { lat: number; lng: number } {
  const angularDistance = distanceMeters / EARTH_RADIUS_METERS;
  const brng = (bearingDegrees * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lng1 = (lng * Math.PI) / 180;
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) +
      Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(brng),
  );
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(brng) * Math.sin(angularDistance) * Math.cos(lat1),
      Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2),
    );
  return { lat: (lat2 * 180) / Math.PI, lng: (lng2 * 180) / Math.PI };
}

export function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function bearingDegrees(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos((lat2 * Math.PI) / 180);
  const x =
    Math.cos((lat1 * Math.PI) / 180) * Math.sin((lat2 * Math.PI) / 180) -
    Math.sin((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function computeGpsPeerFields(
  self: SelfPosition,
  gpsPeer: GpsPeer,
  now: number,
): { gpsDistance: number; gpsBearingDeg: number; lastSeenAt: number } {
  const gpsDistance = haversineMeters(self.lat, self.lng, gpsPeer.lat, gpsPeer.lng);
  const gpsBearingDeg = bearingDegrees(self.lat, self.lng, gpsPeer.lat, gpsPeer.lng);
  const lastSeenAt =
    Number.isFinite(gpsPeer.lastKnownAt) && gpsPeer.lastKnownAt > 0 ? gpsPeer.lastKnownAt : now;
  return { gpsDistance, gpsBearingDeg, lastSeenAt };
}

/** Minimal NearbyPeer for a Supabase/BLE-GPS fix with no BLE registry entry. */
export function createGpsOnlyNearbyPeer(
  gpsPeer: GpsPeer,
  self: SelfPosition,
  now: number,
): NearbyPeer {
  const { gpsDistance, gpsBearingDeg, lastSeenAt } = computeGpsPeerFields(self, gpsPeer, now);
  return {
    peerId: gpsPeer.id,
    rssi: 0,
    smoothedRssi: 0,
    estimatedDistance: 0,
    zone: null,
    lastSeenAt,
    gpsDistance,
    gpsBearingDeg,
    gpsSource: gpsPeer.source,
  };
}

export function enrichNearbyPeerWithGps(
  peer: NearbyPeer,
  gpsPeer: GpsPeer,
  self: SelfPosition,
  now: number,
): NearbyPeer {
  const { gpsDistance, gpsBearingDeg, lastSeenAt } = computeGpsPeerFields(self, gpsPeer, now);
  return {
    ...peer,
    gpsDistance,
    gpsBearingDeg,
    lastSeenAt: Math.max(peer.lastSeenAt, lastSeenAt),
    gpsSource: gpsPeer.source,
  };
}

/** Apply GPS fix to peer records (Lynx gpsPeersToLynxUsers, without display names). */
export function applyGpsToPeers(
  self: SelfPosition | null,
  gpsPeers: GpsPeer[],
  peers: Map<PeerId, NearbyPeer>,
  now: number,
): Map<PeerId, NearbyPeer> {
  if (!self || !Number.isFinite(self.lat) || !Number.isFinite(self.lng)) {
    return peers;
  }

  const next = new Map(peers);
  const gpsIds = gpsPeers.map((p) => p.id);
  for (const gpsPeer of gpsPeers) {
    if (!Number.isFinite(gpsPeer.lat) || !Number.isFinite(gpsPeer.lng)) {
      continue;
    }

    // The exact id first; a cut-short key only when it fits this person and nobody else in the fix list
    // (first-prefix-wins put one friend's GPS on another's Bluetooth entry).
    let existingKey: PeerId | undefined = next.has(gpsPeer.id) ? gpsPeer.id : undefined;
    if (existingKey === undefined) {
      for (const id of next.keys()) {
        if (resolveCanonicalPeerId(id, gpsIds) === gpsPeer.id) {
          existingKey = id;
          break;
        }
      }
    }
    const existing = existingKey !== undefined ? next.get(existingKey) : undefined;
    if (existing) {
      next.set(existingKey!, enrichNearbyPeerWithGps(existing, gpsPeer, self, now));
    }
  }
  return next;
}
